import { ofetch, FetchError } from 'ofetch';
import type { $Fetch } from 'ofetch';
import { ApiErrorResponse, ApiSuccessResponse } from '@/shared/types/api.types';
import { useServerStore } from '@/shared/stores/server.store';

/**
 * Creates a pre-configured AJAX client using ofetch.
 */
export const createAjaxClient = (signal?: AbortSignal): $Fetch => {
    const { baseUrl } = useServerStore().Server!.networking;
    return ofetch.create({
        baseURL: baseUrl,
        responseType: 'json',
        signal,
    });
};

// ─── XHR client ──────────────────────────────────────────────────────────────

export type XhrClientOptions = {
    method: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
    body?: FormData | XMLHttpRequestBodyInit;
    query?: Record<string, string | number | boolean | null | undefined>;
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
};

export type XhrClient = <T>(path: string, options: XhrClientOptions) => Promise<T>;

/**
 * Creates a pre-configured XHR client.
 * Use this instead of createAjaxClient when upload progress tracking is needed,
 * since the fetch API does not expose upload progress events.
 */
export const createXhrClient: () => XhrClient = () => {
    const { baseUrl } = useServerStore().Server!.networking;

    return <T>(path: string, options: XhrClientOptions): Promise<T> => {
        return new Promise((resolve, reject) => {
            const params = new URLSearchParams();
            if (options.query) {
                for (const [key, value] of Object.entries(options.query)) {
                    if (value !== undefined && value !== null) {
                        params.set(key, String(value));
                    }
                }
            }

            const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
            const url = params.size > 0
                ? `${base}${path}?${params}`
                : `${base}${path}`;

            const xhr = new XMLHttpRequest();
            xhr.open(options.method, url);
            xhr.withCredentials = true;
            xhr.responseType = 'json';

            if (options.onProgress) {
                xhr.upload.addEventListener('progress', (e) => {
                    if (e.lengthComputable) {
                        options.onProgress!(Math.round((e.loaded / e.total) * 100));
                    }
                });
            }

            xhr.addEventListener('load', () => {
                const data = xhr.response as T & { err_msg?: string };
                if (xhr.status < 200 || xhr.status >= 300) {
                    reject(buildXhrError(data?.err_msg ?? xhr.statusText, xhr.status, data));
                    return;
                }
                resolve(data);
            });

            xhr.addEventListener('error', () => reject(buildXhrError('Network error', 0, {})));
            xhr.addEventListener('abort', () => reject(buildXhrError('Upload aborted', 0, {})));

            if (options.signal) {
                options.signal.addEventListener('abort', () => xhr.abort());
            }

            xhr.send(options.body);
        });
    };
};

function buildXhrError(message: string, statusCode: number, data: object): Error {
    const err = new Error(message) as Error & {
        statusCode: number;
        response: {
            _data: object;
        };
    };
    err.statusCode = statusCode;
    err.response = {
        _data: data
    };
    return err;
}

/**
 * Creates a pre-configured REST client using ofetch.
 */
export const createRestClient: (accessToken: string | null) => $Fetch = (accessToken: string | null = null) => {
    const { apiUrl } = useServerStore().Server!.networking;

    return ofetch.create({
        baseURL: apiUrl,
        responseType: 'json',
        async onRequest({ options }) {
            // TODO: implement and use an auth store
            // this store should hold the token informations (access, refresh, expire)
            // that way, we could check here if the token is valid and otherwise, refresh it seamlessly
            // something like:
            // const authStore = useAuthStore();
            // if (!authStore.isLoggedIn) return;
            // await authStore.refresh();
            // const accessToken = authStore.accessToken;
            if (accessToken) {
                options.headers = new Headers(options.headers);
                options.headers.set('Authorization', `Bearer ${accessToken}`);
            }
        },
    });
};

/**
 * Safely executes an API call and standardizes the error response.
 * @param promise A promise from an API call
 * @returns A success or error response
 */
export async function safeApiCall<T = null>(promise: Promise<T>): Promise<ApiSuccessResponse<T> | ApiErrorResponse> {
    try {
        const result = await promise;
        if (result === undefined || result === null || typeof result === 'string') {
            return new ApiSuccessResponse();
        }
        assertSuccessResponse(result);

        return new ApiSuccessResponse(result as T);
    } catch (error) {
        return handleApiError(error as FetchError);
    }
}

/**
 * Handles known and unknown errors from API calls.
 * @param error The thrown error
 * @throws If critical or unknown error
 */
function handleApiError(error: FetchError): ApiErrorResponse | never {
    const errorText = error.response?._data?.err_msg || error.response?._data?.msg || error.message;
    const details = error.response?._data?.details || error.response?._data?.err_details || error.response?._data?.err_msg || error.response?._data?.msg || error.message;
    throw new ApiErrorResponse(errorText, details, error.statusCode);
}

/**
 * Asserts that the result is a successful API response.
 * Throws an error otherwise.
 * @param result The response to check
 */
function assertSuccessResponse<T extends object | null>(result: unknown): asserts result is ApiSuccessResponse<T> {
    if (typeof result === 'object' && result !== null && 'success' in result && (result as { success: unknown }).success === false) {
        const resultObj = result as { err_msg?: string };
        const error = new Error(resultObj.err_msg || 'Server returned an error.');
        (error as Error & { isResponseError: boolean }).isResponseError = true;
        throw error;
    }
}

// Testing for tanstack/vue-query overrides (includes ApiErrorResponse)
// export function useApiMutation<TData, TVariables = void, TContext = unknown>(
//     options: UseMutationOptions<TData, ApiErrorResponse, TVariables, TContext>
// ) {
//     return useMutation<TData, ApiErrorResponse, TVariables, TContext>(options);
// }
